import { Box, Stack, Theme } from '@mui/material';
import { Employee } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PoolButton, PoolChip, PoolFormattedMessage, PoolModal, PoolSelect, SelectOption } from '@synergy/components';
import { getLocalizedString } from '@synergy/utils';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';

import { localesSelector } from '../../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

const useStyles = makeStyles((theme: Theme) => ({
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(4),
    },
    chip: {
        fontSize: 12,
        fontWeight: 'bold',
        height: theme.spacing(4),
        padding: theme.spacing(),
        borderRadius: 18,
        margin: theme.spacing(0.5),

        '& svg': {
            color: 'rgba(255, 255, 255, 0.7)',
            margin: '0px 4px 0px 0px !important',
        },
        '& > span': {
            padding: '0 8px',
            textAlign: 'center',
        },
    },
}));

export interface RemoveEmployeeModalProps {
    isVisible: boolean;
    employees: Employee[];
    employee: Employee | null;
    bonusContactCount: number;
    onConfirm: (
        reassignMainContactId?: number,
        reassignTopicsContactId?: number,
        reassignBonusContactId?: number
    ) => void;
    onCancel: () => void;
}

export const RemoveEmployeeModal = (props: RemoveEmployeeModalProps) => {
    const classes = useStyles();
    const { bonusContactCount, isVisible, onConfirm, onCancel } = props;
    const [newMainContactId, setNewMainContactId] = useState<number | null>(null);
    const [newBonusContactId, setNewBonusContactId] = useState<number | null>(null);
    const [newTopicsContactId, setNewTopicsContactId] = useState<number | null>(null);
    const locales = useAppSelector(localesSelector);

    useEffect(() => {
        setNewMainContactId(null);
        setNewTopicsContactId(null);
        setNewBonusContactId(null);
    }, [props.employees, props.employee]);

    const newContactOptions = props.employees
        .filter(employee => employee.id !== props.employee?.id)
        .map<SelectOption>(employee => ({
            value: employee.id,
            displayValue: employee.fullName,
        }));

    const showTopicsContactSelect = newContactOptions.length > 0 && (props.employee?.topics ?? []).length > 0;
    const showMainContactSelect = newContactOptions.length > 0 && !!props.employee?.isMainContact;
    const showBonusContactSelect =
        newContactOptions.length > 0 && !!props.employee?.isBonusContact && bonusContactCount === 1;

    const handleConfirm = () => {
        const reassignMainContactId = newMainContactId ?? undefined;
        const reassignTopicsContactId = newTopicsContactId ?? undefined;
        const reassignBonusContactId = newBonusContactId ?? undefined;
        onConfirm(reassignMainContactId, reassignTopicsContactId, reassignBonusContactId);
    };

    return (
        <PoolModal
            title={<FormattedMessage id="app.contactsAndOrganizations.detail.removePerson" />}
            isVisible={isVisible}
            actionChildren={[
                <PoolButton key="1" variant="text" styleType="firstLevel" onClick={onCancel}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>,
                <PoolButton
                    key="2"
                    color="secondary"
                    styleType="firstLevel"
                    onClick={handleConfirm}
                    disabled={showMainContactSelect && !newMainContactId}
                >
                    <FormattedMessage id="app.common.remove" />
                </PoolButton>,
            ]}
            handleClose={onCancel}
            hasModalTypography={true}
        >
            <div className={classes.content}>
                {!showMainContactSelect && !showBonusContactSelect && (
                    <Box component="span">
                        <PoolFormattedMessage
                            id={'app.contactsAndOrganizations.detail.removePersonMessage'}
                            values={{
                                name: props.employee?.fullName,
                            }}
                        />
                    </Box>
                )}

                {showMainContactSelect && (
                    <Stack gap={3}>
                        <Box component="span">
                            <PoolFormattedMessage
                                id={'app.contactsAndOrganizations.detail.removePersonMessageMainContact'}
                                values={{
                                    name: props.employee?.fullName,
                                }}
                            />
                        </Box>
                        <PoolSelect
                            displayEmpty={true}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectMainContact" />}
                            fullWidth={true}
                            value={newMainContactId}
                            onValueChange={setNewMainContactId}
                            options={newContactOptions}
                            noSorting={true}
                            disallowDefaultValue={true}
                        />
                    </Stack>
                )}

                {showBonusContactSelect && (
                    <Stack gap={3}>
                        <Box component="span">
                            <PoolFormattedMessage
                                id={'app.contactsAndOrganizations.detail.removePersonMessageBonus'}
                                values={{
                                    name: props.employee?.fullName,
                                }}
                            />
                        </Box>
                        <PoolSelect
                            displayEmpty={true}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectBonusContact" />}
                            fullWidth={true}
                            value={newBonusContactId}
                            onValueChange={setNewBonusContactId}
                            options={newContactOptions}
                            noSorting={true}
                            disallowDefaultValue={true}
                        />
                    </Stack>
                )}

                {showTopicsContactSelect && (
                    <Stack gap={3}>
                        <Box component="span">
                            <PoolFormattedMessage
                                id={'app.contactsAndOrganizations.detail.removePersonMessage.topics'}
                                values={{
                                    name: props.employee?.fullName,
                                }}
                            />
                            <Stack direction="row" justifyContent="center" gap={1} sx={{ mt: 1, mb: 4 }}>
                                {props.employee?.topics?.map(topic => (
                                    <PoolChip
                                        key={topic.id}
                                        label={getLocalizedString(locales, topic.title)}
                                        className={classes.chip}
                                    />
                                ))}
                            </Stack>
                            <PoolFormattedMessage
                                id={'app.contactsAndOrganizations.detail.removePersonMessage.topics.reassign'}
                                values={{
                                    name: props.employee?.fullName,
                                }}
                            />
                        </Box>
                        <PoolSelect
                            displayEmpty={true}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.selectContact" />}
                            fullWidth={true}
                            value={newTopicsContactId}
                            onValueChange={setNewTopicsContactId}
                            options={newContactOptions}
                            noSorting={true}
                            disallowDefaultValue={true}
                        />
                    </Stack>
                )}
            </div>
        </PoolModal>
    );
};
